import { ReactNode, useCallback, useEffect, useState } from 'react';
import { MdClose, MdHelpOutline, MdSearch } from 'react-icons/md';
import { MultiValueRemoveProps } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  appendToken,
  FilterColumn,
  getNextOptions,
  isLike,
  isValidNewOption,
  makeFilter,
  makeNewOption,
  Option,
  removeToken
} from '@blisspointmedia/bpm-types/dist/FilterBar';
import { Size, SizeType } from '../designConstants';
import { IconButton } from '../IconButton';
import { ADVANCED_FILTER_STORAGE_KEY, useFilterBarTokens } from './useFilterBarTokens';

interface AdvancedFilterBarProps<T = object> {
  defaultTokens?: Option[];
  disableAutoFilter?: boolean;
  dismissMalformed?: () => void;
  dynamicTokens?: Option[];
  initialValue?: string;
  lines: T[];
  onChange?: (newTokens: Option[]) => void;
  onFilter: (filter: (line: T) => boolean, tokens: Option[]) => void;
  options: string[] | FilterColumn[];
  placeholder?: string;
  setDynamicTokens?: (dynamicTokens: string[]) => void;
  setMalformed?: (error: string | Error) => void;
  setShowHelp?: (show: boolean) => void;
  size?: SizeType;
  // Disambiguates the sessionStorage key when a page renders more than one filterable table at the
  // same pathname -- see useFilterBarTokens.ts.
  storageKeySuffix?: string;
  tokens?: Option[];
  useLocalStorage?: boolean;
  filterOnModeChange?: boolean;
}

const MultiValueRemove = ({ data, innerProps }: MultiValueRemoveProps<Option, true>) =>
  data.isFixed ? (
    <div />
  ) : (
    <div className='filterBarSelect__multi-value__remove' {...innerProps}>
      <MdClose />
    </div>
  );

export const AdvancedFilterBar = <T extends object>({
  defaultTokens = [],
  disableAutoFilter,
  dismissMalformed = () => {},
  dynamicTokens = [],
  initialValue,
  lines,
  onChange = (_newTokens: Option[]) => {},
  onFilter = (_filter: (line: T) => boolean, _tokens: Option[]) => {},
  options,
  placeholder = 'Search Statement',
  setDynamicTokens = (_dt: string[]) => {},
  setMalformed = _error => {},
  setShowHelp = _show => {},
  size = Size.LG,
  storageKeySuffix,
  tokens,
  useLocalStorage = true,
  filterOnModeChange = true
}: AdvancedFilterBarProps<T>): ReactNode => {
  const [initialized, setInitialized] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // This takes our tokens and creates a filter. If the makeFilter function fails, we don't set a filter and display
  // an error message (unless displayMalformed is false).
  const submitFilter = useCallback(
    (filterTokens: Option[] = [], displayMalformed = true) => {
      let ourFilter;
      if (!filterTokens.length) {
        ourFilter = () => true;
      } else {
        try {
          ourFilter = makeFilter(filterTokens, options);
        } catch (err) {
          if (displayMalformed) {
            setMalformed(err.message || err);
          }
          return;
        }
      }
      dismissMalformed();
      if (filterOnModeChange || initialized) {
        onFilter(ourFilter, filterTokens);
      }
      setInitialized(true);
    },
    [
      setMalformed,
      dismissMalformed,
      onFilter,
      options,
      initialized,
      setInitialized,
      filterOnModeChange
    ]
  );

  // Since `displayMalformed` is false, then this will submit the filter if it's valid, and do nothing otherwise.
  const submitFilterIfValid = useCallback(
    (filterTokens: Option[]) => submitFilter(filterTokens, false),
    [submitFilter]
  );

  const { input, setInput, ourTokens, setTokens } = useFilterBarTokens({
    defaultTokens,
    disableAutoFilter,
    initialValue,
    onChange,
    onAutoFilter: submitFilterIfValid,
    storageKey: ADVANCED_FILTER_STORAGE_KEY,
    storageKeySuffix,
    tokens,
    useLocalStorage
  });

  // We only want this to run on mount, so we don't want it to rerun whenever the tokens or anything else changes
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    submitFilter(ourTokens);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Turns a selected option ('elem') into a filter token
  const addOption = useCallback(
    (elem: Option) => {
      setInput('');
      setTokens(appendToken(ourTokens, elem, true));
    },
    [setInput, setTokens, ourTokens]
  );

  // Removes the last token / selected option in the filter
  const popOption = useCallback(() => {
    setInput('');
    setTokens(removeToken(ourTokens, undefined, true));
  }, [setInput, setTokens, ourTokens]);

  const clearFilter = useCallback(() => {
    setTokens([]);
    submitFilter();
    dismissMalformed();
  }, [setTokens, submitFilter, dismissMalformed]);

  // Force set tokens if dynamic tokens has been passed (tokens have been set programmatically in a parent)
  useEffect(() => {
    if (dynamicTokens.length > 0) {
      // Clear dynamically set token so it's not set again every rerender
      setDynamicTokens([]); // App completely bricks if we remove this ?
      // Update filter bar
      setTokens(dynamicTokens);
    }
  }, [dynamicTokens, setDynamicTokens, setTokens]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (menuOpen) {
        return;
      }
      if (e.key === 'Enter') {
        submitFilter(ourTokens);
        e.preventDefault();
      }
    },
    [menuOpen, submitFilter, ourTokens]
  );

  const handleChange = useCallback(
    (
      elems: readonly Option[],
      { action, removedValue }: { action: string; removedValue?: Option }
    ) => {
      const mutableElems = [...elems];
      switch (action) {
        case 'remove-value':
        case 'pop-value':
          if (removedValue && !removedValue.isFixed) {
            popOption();
          }
          return;
        case 'create-option':
          {
            const last = mutableElems.pop();
            if (last) {
              addOption(makeNewOption(last.label, mutableElems.length));
            }
          }
          return;
        case 'clear':
          clearFilter();
          return;
        default:
          break;
      }
      if (!mutableElems.length) {
        setInput('');
        setTokens([]);
        return;
      }
      const last = mutableElems.pop();
      addOption(last!);
    },
    [popOption, addOption, clearFilter, setInput, setTokens]
  );

  return (
    <>
      <div className='filter-bar-component'>
        <div className='filter-bar-select-container'>
          <CreatableSelect<Option, true>
            blurInputOnSelect={false}
            classNamePrefix='filter-bar-select'
            closeMenuOnSelect={false}
            components={{
              DropdownIndicator: null,
              MultiValueRemove
            }}
            formatCreateLabel={labelText => `Search for "${labelText}"`}
            getOptionLabel={option => option.label || '\u00a0'}
            inputValue={input}
            isClearable={false}
            isMulti
            isValidNewOption={(elem: string) => isValidNewOption(elem, ourTokens)}
            noOptionsMessage={() =>
              isLike(ourTokens) ? 'Start typing what you want to filter for' : 'No options'
            }
            options={getNextOptions(ourTokens, options, lines)}
            placeholder={placeholder}
            value={ourTokens}
            onMenuClose={() => setMenuOpen(false)}
            onMenuOpen={() => {
              dismissMalformed();
              setMenuOpen(true);
            }}
            onKeyDown={handleKeyDown}
            onInputChange={setInput}
            onChange={handleChange}
            styles={{ container: base => ({ ...base, flexGrow: 1 }) }}
          />
        </div>
        <div className='filter-bar-help'>
          <MdHelpOutline
            className='filter-bar-help-icon'
            onClick={() => {
              setShowHelp(true);
            }}
          />
        </div>
      </div>
      <IconButton
        className='filter-submit'
        onClick={() => {
          submitFilter(ourTokens);
        }}
        size={size}
      >
        <MdSearch className='submit-filter-icon' />
      </IconButton>
    </>
  );
};
